// ProgramContext.jsx
import { createContext, useContext, useEffect, useReducer } from "react";
import { deleteProgram } from "@/api/programApi.jsx";
import useNavi from "@/hook/useNavi.jsx";

const ProgramContext = createContext();

export const ProgramProvider = ({ children }) => {
    const { moveTo } = useNavi();

    const initialState = {
        // 'tabs', 'types', 'activeTab' 등 탭 관련 전역 상태 삭제
        serverData: [],
        isOpen: false,
        selectedItem: null,
        prev: null,
        next: null,
    };

    const programReducer = (state, action) => {
        switch (action.type) {
            case "SET_SERVER_DATA":
                // 이제 필터링은 페이지에서 처리하므로 여기선 그냥 전체 데이터만 저장
                return { ...state, serverData: action.payload };

            case "TOGGLE_MODAL":
                return { ...state, isOpen: !state.isOpen };

            case "SET_SELECTED_ITEM":
                return { ...state, selectedItem: action.payload };

            case "SET_NAVIGATION":
                return { ...state, prev: action.payload.prev, next: action.payload.next };

            default:
                return state;
        }
    };

    const [state, dispatch] = useReducer(programReducer, initialState);

    // ---- 공통 메서드들 ----
    const handleOpen = () => {
        dispatch({ type: "TOGGLE_MODAL" });
    };

    // 카드 클릭(모달 열기)
    // 페이지에서 이미 필터링된 'filteredData'를 인자로 받음
    const handleCardClick = (item, filteredData) => {
        const currentIndex = filteredData.findIndex((data) => data.id === item.id);

        dispatch({ type: "SET_SELECTED_ITEM", payload: item });
        dispatch({
            type: "SET_NAVIGATION",
            payload: {
                prev: filteredData[currentIndex - 1] || null,
                next: filteredData[currentIndex + 1] || null,
            },
        });
        dispatch({ type: "TOGGLE_MODAL" });
    };

    const handlePrev = (filteredData) => {
        if (!state.prev) return;
        const currentIndex = filteredData.findIndex((data) => data.id === state.prev.id);

        const prev = filteredData[currentIndex - 1] || null;
        const next = filteredData[currentIndex + 1] || state.selectedItem;

        dispatch({ type: "SET_SELECTED_ITEM", payload: state.prev });
        dispatch({ type: "SET_NAVIGATION", payload: { prev, next } });
    };

    const handleNext = (filteredData) => {
        if (!state.next) return;
        const currentIndex = filteredData.findIndex((data) => data.id === state.next.id);

        const prev = filteredData[currentIndex - 1] || state.selectedItem;
        const next = filteredData[currentIndex + 1] || null;

        dispatch({ type: "SET_SELECTED_ITEM", payload: state.next });
        dispatch({ type: "SET_NAVIGATION", payload: { prev, next } });
    };

    const handleCardDelete = async (id) => {
        if (!confirm("정말 삭제하시겠습니까?")) return;
        try {
            const response = await deleteProgram(id);
            if (response.status !== 200) throw new Error("Failed to delete program");

            // 여기서도 이제 필터링은 안 하지만, serverData만 업데이트
            const updatedServerData = state.serverData.filter((item) => item.id !== id);
            dispatch({ type: "SET_SERVER_DATA", payload: updatedServerData });
        } catch (error) {
            console.error("Error deleting program:", error);
        }
    };

    const handleCardEdit = (id) => {
        moveTo(`/program/edit/${id}`);
    };

    // 서버 데이터 저장
    const setServerData = (data) => {
        dispatch({ type: "SET_SERVER_DATA", payload: data });
    };

    return (
        <ProgramContext.Provider
            value={{
                ...state,
                handleOpen,
                handleCardClick,
                handlePrev,
                handleNext,
                handleCardEdit,
                handleCardDelete,
                setServerData,
            }}
        >
            {children}
        </ProgramContext.Provider>
    );
};

export const useProgramContext = () => {
    const context = useContext(ProgramContext);
    if (!context) {
        throw new Error("useProgramContext must be used within a ProgramProvider");
    }
    return context;
};